import React, { useEffect } from 'react';
import {
    MessageSquare,
    Globe,
    Headphones,
    Languages,
    Smartphone,
    Map,
    MapPin,
    Clock,
    Layers,
    Brain,
    ThumbsUp,
    Compass,
    Route,
} from 'lucide-react';
import { APP_STORE_URL, GOOGLE_PLAY_URL } from './constants';

const AleTours = () => {
    const colors = {
        primary: '#53354a',
        secondary: '#F5793B',
        accent3: '#f79a6b',
    };

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.opacity = 1;
                    entry.target.style.transform = 'translateY(0)';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('.animate-section').forEach(section => {
            section.style.opacity = 0;
            section.style.transform = 'translateY(20px)';
            section.style.transition = 'opacity 0.6s ease-out, transform 0.6s ease-out';
            observer.observe(section);
        });

        return () => observer.disconnect();
    }, []);

    return (
        <div className="min-h-screen bg-white text-gray-800">
            {/* Hero */}
            <header className="bg-gradient-to-r from-accent2 to-primary min-h-screen flex items-center justify-center py-24 px-8">
                <div className="container max-w-6xl mx-auto flex flex-col items-center">
                    <div className="text-center">
                        <h1 className="text-7xl font-bold text-white mb-6" style={{ fontFamily: "'Montserrat', sans-serif", letterSpacing: '0.1em' }}>ALE</h1>
                        <p className="text-3xl font-light mb-6 tracking-widest" style={{ color: colors.accent3 }}>ASK. LISTEN. EXPLORE.</p>
                        <p className="text-2xl font-semibold mb-4 text-white">The Future of Self-Guided Tourism</p>
                        <p className="text-xl mb-10 max-w-2xl mx-auto text-white opacity-90">All the benefits of a private guide — none of the group tour restrictions</p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a href={APP_STORE_URL} target="_blank" rel="noreferrer" className="btn bg-secondary hover:bg-accent3 text-white font-bold py-3 px-8 rounded-lg transition-all text-center">
                                Download for iOS
                            </a>
                            <a href={GOOGLE_PLAY_URL} target="_blank" rel="noreferrer" className="btn bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary font-bold py-3 px-8 rounded-lg transition-all text-center">
                                Download for Android
                            </a>
                        </div>
                    </div>
                </div>
            </header>

            {/* The Problem */}
            <section className="py-20 px-8 animate-section">
                <div className="container max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold mb-4" style={{ color: colors.primary }}>Tourism Deserves Better</h2>
                        <p className="text-xl max-w-3xl mx-auto text-gray-600">Today's travel experiences are broken in three fundamental ways</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-10">
                        <div className="bg-white p-8 rounded-lg shadow-md text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full" style={{ backgroundColor: colors.primary }}>
                                <Smartphone className="h-8 w-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold mb-4" style={{ color: colors.primary }}>Experience Erosion</h3>
                            <p className="text-gray-600">Too many apps, too much friction. Managing technology becomes the task instead of enjoying the moment.</p>
                        </div>
                        <div className="bg-white p-8 rounded-lg shadow-md text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full" style={{ backgroundColor: colors.primary }}>
                                <Clock className="h-8 w-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold mb-4" style={{ color: colors.primary }}>Time & Location</h3>
                            <p className="text-gray-600">Fixed schedules, limited languages, and guides that can't be everywhere at once. Your trip shouldn't be on someone else's clock.</p>
                        </div>
                        <div className="bg-white p-8 rounded-lg shadow-md text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full" style={{ backgroundColor: colors.primary }}>
                                <Globe className="h-8 w-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold mb-4" style={{ color: colors.primary }}>One Story Fits All</h3>
                            <p className="text-gray-600">Everyone gets the same scripted narrative regardless of their interests, background, or curiosity.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* The Solution */}
            <section className="py-20 px-8 animate-section bg-gray-50">
                <div className="container max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold mb-4" style={{ color: colors.primary }}>A Better Way to Explore</h2>
                        <p className="text-xl max-w-3xl mx-auto text-gray-600">ALE transforms every trip into a personal discovery journey</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-10">
                        <div className="bg-white p-8 rounded-lg shadow-md text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full" style={{ backgroundColor: colors.secondary }}>
                                <Compass className="h-8 w-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold mb-4" style={{ color: colors.primary }}>A Seamless Journey</h3>
                            <p className="text-gray-600">Unified planning, movement, and memory — keeping you in the moment instead of juggling apps and maps.</p>
                        </div>
                        <div className="bg-white p-8 rounded-lg shadow-md text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full" style={{ backgroundColor: colors.secondary }}>
                                <Route className="h-8 w-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold mb-4" style={{ color: colors.primary }}>Flexible & Independent</h3>
                            <p className="text-gray-600">Start when you want, skip what doesn't interest you, and dive deep into what fascinates you.</p>
                        </div>
                        <div className="bg-white p-8 rounded-lg shadow-md text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full" style={{ backgroundColor: colors.secondary }}>
                                <MessageSquare className="h-8 w-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold mb-4" style={{ color: colors.primary }}>Beyond the Tourist Script</h3>
                            <p className="text-gray-600">Personalized storytelling in your language, adapting to your interests and pace.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* How ALE Works */}
            <section className="py-20 px-8 animate-section">
                <div className="container max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold mb-4" style={{ color: colors.primary }}>How ALE Works</h2>
                        <p className="text-xl max-w-3xl mx-auto text-gray-600">Three simple steps, endlessly rich experiences</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-10">
                        <div className="bg-white p-8 rounded-lg shadow-md text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full" style={{ backgroundColor: colors.secondary }}>
                                <MessageSquare className="h-8 w-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold mb-4" style={{ color: colors.primary }}>ASK</h3>
                            <p className="text-gray-600">Ask anything about what you see around you — history, architecture, local stories, hidden details. ALE knows where you are and what's nearby.</p>
                        </div>
                        <div className="bg-white p-8 rounded-lg shadow-md text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full" style={{ backgroundColor: colors.secondary }}>
                                <Headphones className="h-8 w-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold mb-4" style={{ color: colors.primary }}>LISTEN</h3>
                            <p className="text-gray-600">Get rich, personalized answers in your language. Listen while you walk, keeping your eyes free to take in the surroundings.</p>
                        </div>
                        <div className="bg-white p-8 rounded-lg shadow-md text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full" style={{ backgroundColor: colors.secondary }}>
                                <Map className="h-8 w-8 text-white" />
                            </div>
                            <h3 className="text-xl font-bold mb-4" style={{ color: colors.primary }}>EXPLORE</h3>
                            <p className="text-gray-600">Follow themed walking routes or wander freely. Discover hidden stories and fascinating details as you move at your own pace.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Key Features */}
            <section className="py-20 px-8 animate-section bg-gray-50">
                <div className="container max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold mb-4" style={{ color: colors.primary }}>Key Features</h2>
                    </div>
                    <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-8">
                        <div className="bg-white p-6 rounded-lg shadow-md text-center">
                            <MapPin size={36} className="mx-auto mb-4" style={{ color: colors.secondary }} />
                            <h3 className="font-bold mb-2" style={{ color: colors.primary }}>Location-Aware</h3>
                            <p className="text-sm text-gray-600">Knows exactly where you are and what's around you</p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md text-center">
                            <Brain size={36} className="mx-auto mb-4" style={{ color: colors.secondary }} />
                            <h3 className="font-bold mb-2" style={{ color: colors.primary }}>Natural Language AI</h3>
                            <p className="text-sm text-gray-600">Ask questions naturally — like talking to a knowledgeable friend</p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md text-center">
                            <Smartphone size={36} className="mx-auto mb-4" style={{ color: colors.secondary }} />
                            <h3 className="font-bold mb-2" style={{ color: colors.primary }}>Any Smartphone</h3>
                            <p className="text-sm text-gray-600">Works on iOS and Android — just download and go</p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md text-center">
                            <Languages size={36} className="mx-auto mb-4" style={{ color: colors.secondary }} />
                            <h3 className="font-bold mb-2" style={{ color: colors.primary }}>50+ Languages</h3>
                            <p className="text-sm text-gray-600">Experience any destination in your preferred language</p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-md text-center">
                            <Route size={36} className="mx-auto mb-4" style={{ color: colors.secondary }} />
                            <h3 className="font-bold mb-2" style={{ color: colors.primary }}>Walking Routes</h3>
                            <p className="text-sm text-gray-600">Themed routes curated for every type of traveler</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* For Travelers */}
            <section className="py-20 px-8 animate-section bg-gray-50">
                <div className="container max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold mb-4" style={{ color: colors.primary }}>For Every Traveler</h2>
                    </div>
                    <div className="grid md:grid-cols-2 gap-12">
                        <div className="bg-white p-8 rounded-lg shadow-md relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-24 h-24 bg-secondary opacity-10 rounded-full -mt-8 -mr-8"></div>
                            <h3 className="text-xl font-bold mb-6" style={{ color: colors.primary }}>Casual Visitors</h3>
                            <ul className="space-y-4">
                                <li className="flex items-start">
                                    <div className="mr-4 mt-1" style={{ color: colors.secondary }}>
                                        <Clock size={20} />
                                    </div>
                                    <div>
                                        <p className="font-medium mb-1" style={{ color: colors.primary }}>Your Time, Your Way</p>
                                        <p className="text-gray-600">Focus on what interests you most without following a fixed schedule</p>
                                    </div>
                                </li>
                                <li className="flex items-start">
                                    <div className="mr-4 mt-1" style={{ color: colors.secondary }}>
                                        <Globe size={20} />
                                    </div>
                                    <div>
                                        <p className="font-medium mb-1" style={{ color: colors.primary }}>No Language Barriers</p>
                                        <p className="text-gray-600">Enjoy any destination in your native language, no matter where you are</p>
                                    </div>
                                </li>
                                <li className="flex items-start">
                                    <div className="mr-4 mt-1" style={{ color: colors.secondary }}>
                                        <Layers size={20} />
                                    </div>
                                    <div>
                                        <p className="font-medium mb-1" style={{ color: colors.primary }}>Discover Hidden Stories</p>
                                        <p className="text-gray-600">Learn fascinating details that aren't in any guidebook</p>
                                    </div>
                                </li>
                            </ul>
                        </div>

                        <div className="bg-white p-8 rounded-lg shadow-md relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-24 h-24 bg-secondary opacity-10 rounded-full -mt-8 -mr-8"></div>
                            <h3 className="text-xl font-bold mb-6" style={{ color: colors.primary }}>History Enthusiasts</h3>
                            <ul className="space-y-4">
                                <li className="flex items-start">
                                    <div className="mr-4 mt-1" style={{ color: colors.secondary }}>
                                        <Brain size={20} />
                                    </div>
                                    <div>
                                        <p className="font-medium mb-1" style={{ color: colors.primary }}>Deep Knowledge</p>
                                        <p className="text-gray-600">Ask detailed questions and get in-depth answers about specific periods, events, or artifacts</p>
                                    </div>
                                </li>
                                <li className="flex items-start">
                                    <div className="mr-4 mt-1" style={{ color: colors.secondary }}>
                                        <ThumbsUp size={20} />
                                    </div>
                                    <div>
                                        <p className="font-medium mb-1" style={{ color: colors.primary }}>Personalized Exploration</p>
                                        <p className="text-gray-600">Follow your curiosity without being limited by standard tour narratives</p>
                                    </div>
                                </li>
                                <li className="flex items-start">
                                    <div className="mr-4 mt-1" style={{ color: colors.secondary }}>
                                        <MessageSquare size={20} />
                                    </div>
                                    <div>
                                        <p className="font-medium mb-1" style={{ color: colors.primary }}>Interactive Dialogue</p>
                                        <p className="text-gray-600">Engage in real conversations to explore complex historical topics</p>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Download CTA */}
            <section id="download" className="py-20 px-8 bg-gradient-to-r from-accent2 to-primary text-white">
                <div className="container max-w-6xl mx-auto text-center">
                    <h2 className="text-3xl font-bold mb-4">Start Exploring Today</h2>
                    <p className="text-xl mb-10 max-w-2xl mx-auto opacity-90">Download ALE and turn your next trip into an unforgettable personal discovery</p>
                    <div className="flex flex-col sm:flex-row gap-6 justify-center">
                        <a href={APP_STORE_URL} target="_blank" rel="noreferrer" className="btn bg-secondary hover:bg-accent3 text-white font-bold py-3 px-8 rounded-lg transition-all">
                            Download for iOS
                        </a>
                        <a href={GOOGLE_PLAY_URL} target="_blank" rel="noreferrer" className="btn bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary font-bold py-3 px-8 rounded-lg transition-all">
                            Download for Android
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default AleTours;